/* Login — the employees-only sign-in, behind Cloudflare Access (Google SSO).
   "Sign in" does a HARD navigation to /app so Access intercepts the server
   request and forces Google — a client-side router push would bypass Access
   entirely (the SPA would render the shell without ever authenticating). */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Compass, Button } from '../../ds';
import { useSession } from '../session';

export function Login() {
  const { status } = useSession();
  const nav = useNavigate();

  // Already have a verified Access session → skip the card into the app
  // (client nav is safe here: RequireAccess will pass).
  useEffect(() => {
    if (status === 'authed') nav('/app', { replace: true });
  }, [status, nav]);

  // HARD navigation — a real server request to /app so Cloudflare Access
  // challenges and hands off to Google. NOT a client-side route push.
  const onSignIn = () => {
    window.location.href = '/app';
  };

  return (
    <div
      data-screen-label="Login"
      style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24, position: 'relative', overflow: 'hidden', background: 'radial-gradient(58% 46% at 50% -4%, var(--primary-soft), transparent 70%), var(--bg)' }}
    >
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, var(--grid-line) 1px, transparent 1.4px)', backgroundSize: '26px 26px', opacity: 0.7, pointerEvents: 'none' }} />
      <div aria-hidden="true" style={{ position: 'absolute', left: '50%', top: -160, width: 520, height: 320, transform: 'translateX(-50%)', background: 'radial-gradient(50% 50% at 50% 50%, var(--glow-iris), transparent 70%)', opacity: 0.5, filter: 'blur(20px)', pointerEvents: 'none' }} />

      <div style={{ position: 'relative', width: '100%', maxWidth: 432, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-2xl)', boxShadow: 'var(--shadow)', padding: '40px 36px 30px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <Compass size={58} glow />
        <div style={{ marginTop: 20, fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--accent-ink)', whiteSpace: 'nowrap' }}>// employees only</div>
        <h1 style={{ margin: '12px 0 0', fontSize: 29, letterSpacing: '-0.025em', lineHeight: 1.15 }}>
          CarrTech<span style={{ color: 'var(--primary)' }}>.AI</span> Mission Control
        </h1>
        <p style={{ margin: '13px 0 0', color: 'var(--muted)', fontSize: 14.5, lineHeight: 1.6, maxWidth: 320 }}>
          The founder’s control room. Sign in with your CarrTech.AI workspace account to take the helm.
        </p>

        <div style={{ width: '100%', marginTop: 26 }}>
          <Button variant="secondary" full size="lg" onClick={onSignIn}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 11, fontSize: 15.5 }}>
              <svg width="19" height="19" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.21-4.74 3.21-8.33z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.28-1.93-6.14-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
                <path fill="#FBBC05" d="M5.86 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.68-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.68 2.84C6.72 7.3 9.14 5.38 12 5.38z" />
              </svg>
              Sign in with Google
            </span>
          </Button>
        </div>

        <div style={{ marginTop: 18, display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3l8 3v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z" />
          </svg>
          carrtech.ai workspace · SSO enforced
        </div>
      </div>
    </div>
  );
}
